import React, {useContext, useState, useMemo} from 'react';
import { Outlet, Link } from "react-router-dom";
import helpers from '../../services/helpers';
import images from '../../services/images';
import { MdHandyman, MdManageAccounts } from "react-icons/md";
import { PiToolboxFill } from "react-icons/pi";
import { LuShipWheel } from "react-icons/lu";
import { FaCalendarDay  } from "react-icons/fa";
import { MdOutlineQrCodeScanner } from "react-icons/md";
import { PiUserSwitchBold, PiTruckFill } from "react-icons/pi";
import { FaMoneyBillTransfer } from "react-icons/fa6";
import ProfileContext from '../../services/profileContext';
import ProtectedComponent from '../../services/protectedComponent';

export default function SideMenuShipBooking({menu}){
    const {
        account
    } = useContext(ProfileContext);
    
    const [openGroup, setOpenGroup] = useState(""); // Tracks which dropdown is toggled

    // Toggle function for parent menus
    const handleToggle = (groupName) => {
        setOpenGroup(openGroup === groupName ? "" : groupName);
    };

    // This determines if a menu section should have the "open" classes
    const getGroupClass = (groupName, subMenuIds) => {
        // 1. Is it open because we clicked it?
        const isManuallyOpen = openGroup === groupName;
        // 2. Is it open because our current URL (menu prop) is inside this group?
        const isUrlOpen = subMenuIds.includes(menu);

        return (isManuallyOpen || isUrlOpen) ? "nav-item menu-is-opening menu-open" : "nav-item";
    };

    // This forces the dropdown to show/hide
    const getTreeviewStyle = (groupName, subMenuIds) => {
        const isOpen = openGroup === groupName || subMenuIds.includes(menu);
        return { display: isOpen ? 'block' : 'none' };
    };

    // 1. Define the IDs for each group
    const inventoryIds = ['manage-item', 'manage-item-form', 'item-brand', 'variant-types', 'manage-inventory-adjustment', 'item-category'];
    const salesIds = ['manage-sales', 'manage-quotations', 'manage-sales-form', 'manage-shipments', 'manage-customers', 'couriers', 'manage-invoices', 'manage-payment-receives', 'manage-sales-return'];
    const purchaseIds = ['manage-purchases', 'manage-purchases-form', 'manage-purchase-receives', 'manage-vendors', 'manage-bills', 'manage-payment-made'];
    const serviceIds = ['manage-service-requests', 'manage-product-units', 'manage-service-schedule'];
    const maintenanceIds = ['manage-ships', 'manage-ports', 'manage-master-schedules'];
    const accountIds = ['users', 'roles'];
    const employeeIds = ['employees'];
    const customerIds = ['manage-customers'];
    const vehicleIds = ['manage-vehicles'];
    const reportIds = ['reports'];
    const settingIds = ['settings'];
    const toolsIds = ['qr-range-generator'];
    const bookingIds = ['manage-bookings'];
    const boardingIds = ['manage-boarding'];
    const scheduleIds = ['manage-schedules'];
    const cashFlowIds = ['cashflow-accounts','cashflow-transactions','cashflow-categories'];

    // 2. Build the menuState object (Ensures no "undefined" errors)
    const menuState = {
        login: {
            active: menu === 'login' ? 'active' : ''
        },
        dashboard: {
            groupClass: getGroupClass("dashboard", ['home', 'customer-dashboard']),
            style: getTreeviewStyle("dashboard", ['home', 'customer-dashboard']),
            active: ['home', 'customer-dashboard'].includes(menu) ? 'active' : ''
        },
        inventory: {
            groupClass: getGroupClass("inventory", inventoryIds),
            style: getTreeviewStyle("inventory", inventoryIds),
            active: inventoryIds.includes(menu) ? 'active' : ''
        },
        sales: {
            groupClass: getGroupClass("sales", salesIds),
            style: getTreeviewStyle("sales", salesIds),
            active: salesIds.includes(menu) ? 'active' : ''
        },
        purchases: {
            groupClass: getGroupClass("purchases", purchaseIds),
            style: getTreeviewStyle("purchases", purchaseIds),
            active: purchaseIds.includes(menu) ? 'active' : ''
        },
        service: {
            groupClass: getGroupClass("service", serviceIds),
            style: getTreeviewStyle("service", serviceIds),
            active: serviceIds.includes(menu) ? 'active' : ''
        },
        accounts: {
            groupClass: getGroupClass("accounts", accountIds),
            style: getTreeviewStyle("accounts", accountIds),
            active: accountIds.includes(menu) ? 'active' : ''
        },
        employees: {
            active: employeeIds.includes(menu) ? 'active' : ''
        },
        customers: {
            active: customerIds.includes(menu) ? 'active' : ''
        },
        vehicles: {
            active: vehicleIds.includes(menu) ? 'active' : ''
        },
        reports: {
            active: reportIds.includes(menu) ? 'active' : ''
        },
        settings: {
            active: settingIds.includes(menu) ? 'active' : ''
        },
        bookings: {
            active: bookingIds.includes(menu) ? 'active' : ''
        },
        boarding: {
            active: boardingIds.includes(menu) ? 'active' : ''
        },
        schedules: {
            active: scheduleIds.includes(menu) ? 'active' : ''
        },
        tools: {
            groupClass: getGroupClass("tools", toolsIds),
            style: getTreeviewStyle("tools", toolsIds),
            active: toolsIds.includes(menu) ? 'active' : ''
        },
        maintenance: {
            groupClass: getGroupClass("maintenance", maintenanceIds),
            style: getTreeviewStyle("maintenance", maintenanceIds),
            active: maintenanceIds.includes(menu) ? 'active' : ''
        },
        cashflow: {
            groupClass: getGroupClass("cashflow", cashFlowIds),
            style: getTreeviewStyle("cashflow", cashFlowIds),
            active: cashFlowIds.includes(menu) ? 'active' : ''
        },
    };

    return (
        <>
        {/* Main Sidebar Container */}
        <aside className="main-sidebar elevation-4 sidebar-light-dark">
        {/* <aside className="main-sidebar sidebar-dark-light elevation-4"> */}
            {/* Brand Logo */}
            <Link className="brand-link d-flex align-items-center" to="/">
                <img src={images.etyel_icon} alt="Icon"  className="brand-image" />
                <img className='brand-text' src={images.etyel_text_only} alt="Logo" />
                {/* <div className='brand-text'>WV SERVICES</div> */}
                {/* <small className='ml-1'><sup>BETA</sup></small> */}
            </Link>

            {/* Sidebar */}
            
            <div className="sidebar">
                {/* Sidebar Menu */}
                <nav className="mt-2">
                    { !helpers.isAuthenticated() && (
                        <ul className="nav nav-pills nav-sidebar flex-column nav-collapse-hide-child nav-child-indent nav-compact" role="menu">
                            <li className="nav-item">
                                <Link 
                                    to="/login" 
                                    className={`nav-link ${menu === 'login' ? 'active' : ''}`}
                                >
                                    <i className="fas fa-sign-in-alt mr-3"></i>
                                    <p>Login</p>
                                </Link>
                            </li>
                        </ul>
                    )}
                    
                    { 
                        helpers.isAuthenticated() ?
                        <ul className="nav nav-pills nav-sidebar flex-column nav-collapse-hide-child nav-child-indent nav-compact" data-widget="treeviews" role="menu" data-accordion="false">
                            <li className={getGroupClass("dashboard", ['home', 'customer-dashboard'])}>
                                <a 
                                    href="#" 
                                    className={`nav-link nav-link-main ${['home', 'customer-dashboard'].includes(menu) ? 'active' : ''}`} 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleToggle("dashboard");
                                    }}
                                >
                                    <i className="nav-icon fas fa-tachometer-alt mr-2" />
                                    <p>
                                        Dashboard 
                                        <i className="right fas fa-angle-left" />
                                    </p>
                                </a>
                                <ul className="nav nav-treeview" style={getTreeviewStyle("dashboard", ['home', 'customer-dashboard'])}>
                                    <li className="nav-item">
                                        <Link to="/dashboard" className={`nav-link ${menu === 'home' ? 'active' : ''}`}>
                                            <p className='ml-3'>Home</p>
                                        </Link>
                                    </li>
                                </ul>
                            </li>

                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","shipBookingView","shipBookingCreate","shipBookingEdit","shipBookingDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.bookings.active}`} to="/ship-booking/bookings">
                                        <i className="nav-icon fas fa-calendar-check mr-2" />
                                        <p>Bookings</p>
                                    </Link>
                                </li>
                            </ProtectedComponent>

                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","voyageScheduleView","voyageScheduleCreate", "voyageScheduleEdit","voyageScheduleDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.schedules.active}`} to="/ship-booking/schedules">
                                        <i className="nav-icon fas fa-calendar-alt mr-2" />
                                        <p>Schedules</p>
                                    </Link>
                                </li>
                            </ProtectedComponent>

                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","shipOnBoarding"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.boarding.active}`} to="/ship-booking/boarding">
                                        <MdOutlineQrCodeScanner size={25} className='mr-2'/>
                                        <p>Boarding</p>
                                    </Link>
                                </li>
                            </ProtectedComponent>

                            {/* Employees */}
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","customerView","customerCreate","customerEdit","customerDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.customers.active}`} to="/customers/list">
                                        {/* <i className="nav-icon fas fa-users mr-2" /> */}
                                        <PiUserSwitchBold size={25} className='mr-2' />
                                        <p>Customers</p>
                                    </Link>
                                </li>
                            </ProtectedComponent>

                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","vehicleView","vehicleCreate","vehicleEdit","vehicleDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.vehicles.active}`} to="/ship-booking/vehicles">
                                        {/* <i className="nav-icon fas fa-users mr-2" /> */}
                                        <PiTruckFill  size={25} className='mr-2' />
                                        <p>Vehicles</p>
                                    </Link>
                                </li>
                            </ProtectedComponent>

                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","employeeView", "employeeCreate", "employeeEdit", "employeeDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.employees.active}`} to="/hrms/employees/list">
                                        <i className="nav-icon fas fa-users mr-2" />
                                        <p>Employees</p>
                                    </Link>
                                </li>
                            </ProtectedComponent>

                            {/* <ProtectedComponent account={account} requiredPermissions={["superAdmin","cashflowTransactionView", "cashflowTransactionCreate", "cashflowTransactionEdit", "cashflowTransactionDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.cashflow.active}`} to="/cashflow/transactions">
                                        <FaMoneyBillTransfer size={25} className='mr-2'/>
                                        <p>Transactions</p>
                                    </Link>
                                </li>
                            </ProtectedComponent> */}
                            
                            {/* Cash flows */}
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","cashFlowAccountView", "cashFlowAccountCreate", "cashFlowAccountEdit", "cashFlowAccountDelete", "cashFlowCategoryView", "cashFlowCategoryCreate", "cashFlowCategoryEdit", "cashFlowCategoryDelete", "cashFlowTransactionView", "cashFlowTransactionCreate", "cashFlowCTransactionEdit", "cashFlowTransactionDelete"]}>
                                <li className={menuState.cashflow.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.cashflow.active}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("cashflow");
                                        }}
                                    >
                                        <FaMoneyBillTransfer size={25} className='mr-2'/>
                                        <p>
                                            Cash Flow
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.cashflow.style}>
                                        {/* <ProtectedComponent account={account} requiredPermissions={["superAdmin","cashFlowAccountView", "cashFlowAccountCreate", "cashFlowAccountEdit", "cashFlowAccountDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/cashflow/accounts" className={`nav-link ${menu === 'cashflow-accounts' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Accounts</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent> */}
                                        {/* <ProtectedComponent account={account} requiredPermissions={["superAdmin","cashFlowCategoryView", "cashFlowCategoryCreate", "cashFlowCategoryEdit", "cashFlowCategoryDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/cashflow/categories" className={`nav-link ${menu === 'cashflow-categories' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Categories</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent> */}
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","cashFlowTransactionView", "cashFlowTransactionCreate", "cashFlowTransactionEdit", "cashFlowTransactionDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/cashflow/transactions" className={`nav-link ${menu === 'cashflow-transactions' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Transactions</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent>
                            
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","shipView","shipCreate","shipEdit","shipDelete","portView","portCreate","portEdit","portDelete","masterScheduleView","masterScheduleCreate","masterScheduleEdit","masterScheduleDelete"]}>
                                <li className={menuState.maintenance.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.maintenance.active}  d-flex align-items-center`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("maintenance");
                                        }}
                                    >
                                        <MdHandyman size={22} className='mr-3' />
                                        <p>
                                            Maintenance 
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.maintenance.style}>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","shipView","shipCreate","shipEdit","shipDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/ship-booking/ships" className={`nav-link ${menu === 'manage-ships' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Ships</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","portView","portCreate","portEdit","portDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/ship-booking/ports" className={`nav-link ${menu === 'manage-ports' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Ports</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>

                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","masterScheduleView","masterScheduleCreate","masterScheduleEdit","masterScheduleDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/ship-booking/master-schedules" className={`nav-link ${menu === 'manage-master-schedules' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Master Schedules</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent>

                            {/* Accounts Group */}
                            <ProtectedComponent account={account} requiredPermissions={["superAdmin","acount_profileView", "accountCreate", "accountEdit", "accountDelete","userView", "userCreate", "userEdit","userDelete","roleView", "roleCreate", "roleEdit","roleDelete"]}>
                                <li className={menuState.accounts.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.accounts.active}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("accounts");
                                        }}
                                    >
                                        <MdManageAccounts size={25} className='mr-2' />
                                        <p>
                                            Accounts 
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.accounts.style}>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","userView", "userCreate", "userEdit","userDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/account/users/manage" className={`nav-link ${menu === 'users' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Users</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                        
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","roleView", "roleCreate", "roleEdit","roleDelete"]}>
                                            <li className="nav-item">
                                                <Link to="/account/roles/manage" className={`nav-link ${menu === 'roles' ? 'active' : ''}`}>
                                                    <p className='ml-3'>Roles</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent>

                            {/* Tool */}
                            {/* <ProtectedComponent account={account} requiredPermissions={["superAdmin","QRCodeRangeGenerator"]}>
                                <li className={menuState.tools.groupClass}>
                                    <a 
                                        href="#" 
                                        className={`nav-link nav-link-main ${menuState.tools.active}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggle("tools");
                                        }}
                                    >
                                        <PiToolboxFill size={25} className='mr-2' />
                                        <p>
                                            Tools 
                                            <i className="right fas fa-angle-left" />
                                        </p>
                                    </a>
                                    <ul className="nav nav-treeview" style={menuState.tools.style}>
                                        <ProtectedComponent account={account} requiredPermissions={["superAdmin","QRCodeRangeGenerator"]}>
                                            <li className="nav-item">
                                                <Link to="/tools/qr-range-generator" className={`nav-link ${menu === 'qr-range-generator' ? 'active' : ''}`}>
                                                    <p className='ml-3'>QR Range Generator</p>
                                                </Link>
                                            </li>
                                        </ProtectedComponent>
                                    </ul>
                                </li>
                            </ProtectedComponent> */}
                            
                            {/* <ProtectedComponent account={account} requiredPermissions={["superAdmin","categoryView", "categoryCreate", "categoryEdit", "categoryDelete","variantTypeView", "variantTypeCreate", "variantTypeEdit","variantTypeDelete","brandView", "brandCreate", "brandEdit","brandDelete","taxRateView","taxRateCreate","taxRateEdit","taxRateDelete","discountRateView","discountRateCreate","discountRateEdit","discountRateDelete","courierView","courierCreate","courierEdit","courierDelete"]}>
                                <li className="nav-item">
                                    <Link className={`nav-link ${menuState.settings.active}`} to="/settings">
                                        <i className="nav-icon fas fa-cog mr-2" />
                                        <p>Settings</p>
                                    </Link>
                                </li>
                            </ProtectedComponent> */}
                        </ul> : ''
                    }
                </nav>
                
                {helpers.displayVersion()}
                {/* /.sidebar-menu */}
            </div>
            {/* /.sidebar */}
        </aside>
        </>
    )
}
